import { IonIcon, IonItem, IonLabel } from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/button";
import { type UserDevice } from "@/lib/backend/api";

function useFormatRelativeTime() {
  const { t } = useTranslation();

  return (date: string): string => {
    const now = Date.now();
    const then = new Date(date).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) {
      return t("settings.notifications.relative-time.just-now");
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return t("settings.notifications.relative-time.minutes", { count: minutes });
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return t("settings.notifications.relative-time.hours", { count: hours });
    }
    const days = Math.floor(hours / 24);
    if (days < 7) {
      return t("settings.notifications.relative-time.days", { count: days });
    }
    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return t("settings.notifications.relative-time.weeks", { count: weeks });
    }
    const months = Math.floor(days / 30);
    if (months < 12) {
      return t("settings.notifications.relative-time.months", { count: months });
    }
    const years = Math.floor(days / 365);
    return t("settings.notifications.relative-time.years", { count: years });
  };
}

interface DeviceListItemProps {
  device: UserDevice;
  isCurrentDevice: boolean;
  onRemove: (deviceIdentifier: string) => Promise<void>;
}

export function DeviceListItem({ device, isCurrentDevice, onRemove }: DeviceListItemProps) {
  const { t } = useTranslation();
  const formatRelativeTime = useFormatRelativeTime();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await onRemove(device.deviceIdentifier);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <IonItem lines="full" className="--padding-start:0">
      <div className="flex w-full items-center gap-4 py-3">
        {/* Platform Icon */}
        <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
          <PlatformIcon platform={device.platform} />
        </div>

        {/* Device Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <IonLabel className="text-base font-medium text-txt-primary">{device.name}</IonLabel>
            {isCurrentDevice && (
              <span className="dark:bg-brand-900/50 rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-300">
                {t("settings.notifications.other-devices.this-device-badge")}
              </span>
            )}
          </div>
          <p className="text-sm text-txt-secondary">{formatRelativeTime(device.createdAt)}</p>
        </div>

        {/* Remove Button - only for other devices */}
        {!isCurrentDevice && (
          <Button
            size="small"
            fill="outline"
            color="danger"
            onClick={handleRemove}
            disabled={isRemoving}
            className="h-9 shrink-0 text-xs"
          >
            {t("auth.sign-out.dialog.actions.confirm")}
          </Button>
        )}
      </div>
    </IonItem>
  );
}

function PlatformIcon({ platform }: { platform?: string | null }) {
  if (platform === "ios") {
    return (
      <svg
        className="size-6 text-gray-600 dark:text-gray-400"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
    );
  }

  if (platform === "android") {
    return (
      <svg
        className="size-6 text-gray-600 dark:text-gray-400"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 0 0-.83.22l-1.88 3.24a11.46 11.46 0 0 0-8.94 0L5.65 5.67a.643.643 0 0 0-.87-.2c-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z" />
      </svg>
    );
  }

  // Default/unknown platform
  return (
    <svg
      className="size-6 text-gray-600 dark:text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
      />
    </svg>
  );
}
